import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Select, Input, Button, Space, Popconfirm, message, Typography, Tooltip } from 'antd';
import { ReloadOutlined, DeleteOutlined, OrderedListOutlined } from '@ant-design/icons';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text } = Typography;
const { Search } = Input;

export default function RequestLogsManager() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ method: undefined, statusCode: undefined, path: '', platform: 'android-app' });

  const fetchLogs = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, limit };
      if (filters.method) params.method = filters.method;
      if (filters.statusCode) params.statusCode = filters.statusCode;
      if (filters.path) params.path = filters.path;
      if (filters.platform) params.platform = filters.platform;

      const { data } = await adminAPI.getRequestLogs(params);
      if (data.success) {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      message.error('خطا در دریافت لیست لاگ‌های درخواست');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    fetchLogs(page);
  }, [page, filters, fetchLogs]);

  const handleClear = async () => {
    try {
      await adminAPI.clearRequestLogs();
      message.success('تمام لاگ‌های درخواست با موفقیت پاک شدند');
      setPage(1);
      fetchLogs(1);
    } catch (e) {
      message.error('خطا در پاک کردن لاگ‌ها');
    }
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'blue';
      case 'POST': return 'green';
      case 'PUT': return 'orange';
      case 'DELETE': return 'red';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  const columns = [
    {
      title: 'زمان',
      dataIndex: 'createdAt',
      width: 150,
      render: (v) => (
        <Tooltip title={moment(v).format('YYYY-MM-DD HH:mm:ss')}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {moment(v).fromNow()}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'متد',
      dataIndex: 'method',
      width: 90,
      render: (m) => <Tag color={getMethodColor(m)}>{m}</Tag>
    },
    {
      title: 'مسیر',
      dataIndex: 'path',
      render: (p) => <Text copyable style={{ fontSize: 13, fontFamily: 'monospace' }}>{p}</Text>
    },
    {
      title: 'کد وضعیت',
      dataIndex: 'statusCode',
      width: 110,
      render: (s) => <Tag color={getStatusColor(s)}>{s}</Tag>
    },
    {
      title: 'زمان پاسخ',
      dataIndex: 'durationMs',
      width: 120,
      render: (ms) => <Text style={{ color: ms > 1000 ? '#cf1322' : 'inherit' }}>{ms} میلی‌ثانیه</Text>
    },
    {
      title: 'کاربر',
      dataIndex: 'user',
      render: (user) => user ? (
        <Tooltip title={`نام کاربری: ${user.username || 'ندارد'} | تلفن: ${user.phoneNumber || 'ندارد'}`}>
          <Tag color="purple">{user.firstName} {user.lastName}</Tag>
        </Tooltip>
      ) : <Text type="secondary">—</Text>
    },
    {
      title: 'پلتفرم',
      dataIndex: 'platform',
      width: 120,
      render: (p) => <Tag color={p === 'android-app' ? 'red' : 'blue'}>{p === 'android-app' ? 'اندروید' : p}</Tag>
    },
    {
      title: 'آدرس IP',
      dataIndex: 'ip',
      width: 130,
      render: (ip) => <Text type="secondary" style={{ fontSize: 12 }}>{ip || 'نامعلوم'}</Text>
    }
  ];

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="فیلترها"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchLogs(page)}>بروزرسانی</Button>
            <Popconfirm
              title="آیا مطمئن هستید که می‌خواهید تمام لاگ‌ها را پاک کنید؟ این عملیات غیرقابل بازگشت است."
              onConfirm={handleClear}
              okText="بله"
              cancelText="خیر"
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>حذف تمام لاگ‌ها</Button>
            </Popconfirm>
          </Space>
        }
      >
        <Space wrap>
          <Select
            allowClear
            placeholder="متد درخواست"
            style={{ width: 140 }}
            value={filters.method}
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, method: v })); }}
            options={[
              { value: 'GET', label: 'GET' },
              { value: 'POST', label: 'POST' },
              { value: 'PUT', label: 'PUT' },
              { value: 'DELETE', label: 'DELETE' }
            ]}
          />
          <Select
            placeholder="کد وضعیت"
            allowClear
            style={{ width: 140 }}
            value={filters.statusCode}
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, statusCode: v })); }}
            options={[
              { value: 200, label: '200 OK' },
              { value: 201, label: '201 Created' },
              { value: 400, label: '400 Bad Request' },
              { value: 401, label: '401 Unauthorized' },
              { value: 403, label: '403 Forbidden' },
              { value: 404, label: '404 Not Found' },
              { value: 500, label: '500 Server Error' }
            ]}
          />
          <Select
            placeholder="پلتفرم"
            style={{ width: 140 }}
            value={filters.platform}
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, platform: v })); }}
            options={[
              { value: 'android-app', label: 'فقط اندروید' },
              { value: 'web', label: 'فقط وب' }
            ]}
          />
          <Search
            placeholder="جستجوی مسیر (مثال: /api/auth)..."
            style={{ width: 300 }}
            allowClear
            onSearch={(v) => { setPage(1); setFilters(f => ({ ...f, path: v })); }}
          />
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={logs}
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p)
          }}
        />
      </Card>
    </div>
  );
}
